import { Box, Stack, Typography } from '@mui/material';
import { FC, useMemo } from 'react';
import {
  AutocompleteInput,
  BooleanInput,
  Create,
  CreateProps,
  FormDataConsumer,
  ReferenceInput,
  required,
  SaveButton,
  SelectInput,
  SimpleForm,
  TextInput,
  Toolbar,
  ToolbarProps,
  useGetList,
  useTranslate,
} from 'react-admin';
import { FieldValues, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';
import { ReleaseSoftwareComponent, SoftwareComponent, SoftwareRelease } from 'types';
import { COMPONENT_VERSIONS, ComponentVersions, versionFieldName, versionKey } from './componentVersions';
import { SoftwareVersionInput } from './SoftwareVersionInput';

/**
 * A `source` record that is actually usable as a clone. `hardware_id` is what both consumers key
 * off — the read-only hardware input and `cloneVersionsByComponentId` below — so a source without
 * it is not a clone source, only a JSON object that happened to parse.
 */
type CloneSource = Partial<SoftwareRelease> & { hardware_id: number };

/**
 * Whether a parsed `source` can be applied as a clone.
 *
 * Accepts: a plain JSON object carrying `hardware_id` as a finite number greater than zero — what
 * `<CloneButton>` serialises, since the API types the column as an integer id.
 *
 * Rejects: arrays (`[]`, `[{…}]`), `null`, JSON scalars (`1`, `"x"`, `true`), and any object whose
 * `hardware_id` is missing, `null`, `0`, negative, or a string — including a numeric one like
 * `"5"`. A rejected source means no lock and no pre-fill, which is a plain empty create form; the
 * alternative, accepting a shape the rest of the file cannot use, is the read-only-and-empty form
 * this whole change exists to prevent.
 */
const isCloneSource = (value: unknown): value is CloneSource => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;

  const { hardware_id: hardwareId } = value as { hardware_id?: unknown };

  return typeof hardwareId === 'number' && Number.isFinite(hardwareId) && hardwareId > 0;
};

/**
 * The release a clone was started from, read back out of the `source` query parameter that
 * react-admin's `<CloneButton>` serialises the list record into (`CloneRelease` in
 * `SoftwareReleaseList`, minus `name`).
 *
 * react-admin parses the very same parameter itself — `useRecordFromLocation` — and resets the
 * form with it, which is what puts `hardware_id` and `custom` in form state. This hook exists only
 * because the per-component versions do not live in a form field: they sit in the record's
 * `software_components`, and each version input has to be handed its own default.
 *
 * Returning a value therefore guarantees one thing to every caller: a record react-admin will have
 * reset the form with, carrying the `hardware_id` the lock and the version map both depend on.
 * Anything else — a parameter that is not parseable JSON, an array, a scalar, an object without a
 * usable `hardware_id` — yields `undefined` rather than throwing: react-admin itself only logs in
 * those cases, and a create form that crashes on a bad URL is worse than one that pre-fills
 * nothing.
 */
const useCloneSource = (): CloneSource | undefined => {
  const [searchParams] = useSearchParams();
  // `getAll`, not `get`: on a hand-built `?source=a&source=b`, react-admin's
  // `getRecordFromLocation` sees an array, logs, and returns null — so the form is never reset —
  // while `get` would hand back the first value and lock the hardware field on an unfilled form.
  const sources = searchParams.getAll('source');
  const source = sources.length === 1 ? sources[0] : undefined;

  return useMemo(() => {
    if (!source) return undefined;

    try {
      const parsed: unknown = JSON.parse(source);
      return isCloneSource(parsed) ? parsed : undefined;
    } catch {
      return undefined;
    }
  }, [source]);
};

/**
 * The clone source's version selection, keyed by software component id.
 *
 * The API builds a release's `software_components` from its *versions*
 * (`SoftwareReleaseResource::formatUpdateChannels()` in toverzicht-api): each entry is the raw
 * software component model as eager-loaded by `SoftwareReleaseController::$with`
 * (`softwareComponent:id,name` with `hardwareRevisions:id`), plus `software_version_id`. So an
 * entry carries `id`, `name`, `hardware_revisions` and `software_version_id` — and **no**
 * `hardware_ids`, which is added by `SoftwareComponentResource` and therefore only exists on
 * records that came from the `software_components` endpoint. Filtering these entries on
 * `hardware_ids` matched nothing and left every dropdown empty (TVR-1745).
 *
 * Component ids are unique, so the id alone identifies the entry without risk of matching another
 * component. Hardware is checked once, against the release as a whole, rather than once per entry:
 * a clone keeps its source's hardware (the input is read-only), and if that ever stops being true
 * the source's versions do not belong to the selected hardware at all.
 *
 * A per-entry check was available and was deliberately not taken. Each entry's `hardware_revisions`
 * *is* the component's compatible hardware list, so the entries could have been filtered
 * individually. The release-level gate is the safer direction under the one condition that would
 * make the two differ — the hardware lock being lifted: it then yields an empty map and pre-fills
 * nothing, whereas a per-entry predicate would pre-fill a version belonging to the *other*
 * hardware, an id the version `ReferenceInput` does not even offer, sitting in a submitted payload.
 *
 * The mismatch early return is consequently unreachable through the UI today — the hardware input
 * is `readOnly` for a clone, so the form's `hardware_id` is the source's. It is deliberate defence
 * for the day that changes, not dead code; keep it. It is also not worth exporting this function to
 * cover that branch in a unit test.
 */
const cloneVersionsByComponentId = (source: CloneSource | undefined, hardwareId: number) => {
  const versions = new Map<number, number>();

  if (!source || hardwareId == null || source.hardware_id !== Number(hardwareId)) {
    return versions;
  }

  source.software_components?.forEach((component: ReleaseSoftwareComponent) => {
    // An entry whose component row is gone arrives with only `software_version_id` — see
    // `ReleaseSoftwareComponent` in `types/`. It matches no component here and is skipped.
    if (component.id != null && component.software_version_id != null) {
      versions.set(component.id, component.software_version_id);
    }
  });

  return versions;
};

const SoftwareComponentInput = () => {
  const id: number = useWatch({ name: 'hardware_id' });
  const cloneSource = useCloneSource();

  const { data, isLoading } = useGetList<SoftwareComponent>('software_components', {
    filter: {
      hardware_id: id,
    },
  });

  const clonedVersions = useMemo(() => cloneVersionsByComponentId(cloneSource, id), [cloneSource, id]);

  // Returning null until the hardware and its components are known is also what makes the defaults
  // below land: `<AutocompleteInput defaultValue>` applies once, when the input mounts, and by then
  // react-admin has already reset the form with the cloned record, so `hardware_id` — and with it
  // each input's choice set — is settled.
  if (!id || isLoading) return null;

  return (
    <Box sx={{ width: '100%' }}>
      <Stack>
        {data?.map((component) => (
          /*
           * TVR-2156: keyed by component id (`component_versions.c5`), the same namespace
           * `SoftwareReleaseEdit` uses — see `./componentVersions`. The label is a fragment, not the
           * bare name, for the same reason it is one there: react-admin translates a string label,
           * and a component name such as `tover.tafel` is a valid polyglot key path. Passing no
           * label at all is what this form used to do, which made react-admin derive one from the
           * source; against the id-keyed source that would now read "Component versions.c5".
           */
          <SoftwareVersionInput
            key={component.id}
            component={component}
            source={versionFieldName(component)}
            label={<>{component.name}</>}
            defaultValue={clonedVersions.get(component.id)}
          />
        ))}
      </Stack>
    </Box>
  );
};

/**
 * The fields this form may send, an allow-list rather than a deny-list.
 *
 * Cloning is what makes one necessary: react-admin resets the create form with the **whole** record
 * from `?source=`, so the previous `({ tover, ...rest })` spread posted the source release's own
 * `id` and `created_at` — along with `software_version_ids`, `update_channel_ids` and `editable` —
 * straight back to the create endpoint, and any field the release resource grows later would have
 * joined the payload silently (TVR-2151).
 *
 * The set is exactly what `CreateRequest::rules()` validates in toverzicht-api
 * (`app/Toverzicht/Http/Requests/Software/Releases/CreateRequest.php`). `software_components` is
 * not listed because the toolbar assembles it from the hardware's components rather than taking it
 * from form state; `prepareForValidation()` flattens it into the `software_version_ids` that
 * `ValidateVersionsForRelease` and `SoftwareReleaseService::linkSoftwareVersions()` consume, so the
 * source record's own `software_version_ids` must not be echoed back.
 *
 * **Deliberately a separate list from `SoftwareReleaseEdit`'s `SUBMITTED_FIELDS`**, although the two
 * are identical today: they describe two different backend FormRequests, `CreateRequest` and
 * `UpdateRequest`, which are free to diverge. One shared constant would let a change made for one
 * endpoint silently change what is sent to the other. Change one, check the other.
 *
 * **An input added to this form must be added here, or its value is silently not submitted.**
 */
const SUBMITTED_FIELDS = ['name', 'hardware_id', 'hardware_revision_id', 'custom'];

const SoftwareReleaseCreateToolbar = (props: ToolbarProps) => {
  const id: number = useWatch({ name: 'hardware_id' });
  const { data: components, isLoading } = useGetList<SoftwareComponent>('software_components', {
    filter: {
      hardware_id: id,
    },
  });

  return (
    <Toolbar {...props}>
      <SaveButton
        disabled={!id || isLoading}
        transform={(values: FieldValues) => {
          // TVR-2156: read by component id. The predecessor indexed a `tover` object with the
          // second segment of the component's name, which silently produced `undefined` for a
          // component with no dot in its name, a nested object for one with two dots, and one
          // shared key for two components whose second segment matched. `undefined` is not loud
          // here — it just means no version submitted. Full history in `./componentVersions`.
          const selected = (values[COMPONENT_VERSIONS] ?? {}) as ComponentVersions;

          return {
            ...Object.fromEntries(
              SUBMITTED_FIELDS.filter((field) => field in values).map((field) => [field, values[field]]),
            ),
            // No `?? null`: an unselected component must stay absent from the JSON body, which is
            // what `undefined` gives and what this form has always posted. The edit form coalesces
            // to null on purpose — it has to be able to clear a selection the record carries.
            software_components: components?.map((component) => {
              return {
                ...component,
                software_version_id: selected[versionKey(component)],
              };
            }),
          };
        }}
        type="button"
      />
    </Toolbar>
  );
};

export const SoftwareReleaseCreate: FC<CreateProps> = () => {
  // Locked means a record was actually applied, not that a `source` parameter was present: the
  // hook only returns for a source react-admin will have reset the form with and that carries a
  // usable `hardware_id`. Locking on mere presence would leave a read-only, empty, unusable form.
  const clonedRelease = useCloneSource() !== undefined;
  const translate = useTranslate();

  return (
    <Create>
      <SimpleForm toolbar={<SoftwareReleaseCreateToolbar />}>
        <Typography variant="h4">{translate('resources.software_releases.create.title')}</Typography>
        <TextInput source="name" fullWidth validate={required()} />
        <ReferenceInput source="hardware_id" reference="hardware">
          <AutocompleteInput
            optionText="name"
            fullWidth
            readOnly={clonedRelease}
            validate={required()}
            helperText={clonedRelease && translate('resources.software_releases.create.clone_read_only')}
          />
        </ReferenceInput>
        <FormDataConsumer>
          {({ formData, ...rest }) =>
            formData.hardware_id && (
              <ReferenceInput
                source="hardware_revision_id"
                reference="hardware_revisions"
                filter={{ hardware_id: formData.hardware_id }}
              >
                <SelectInput optionText="name" fullWidth validate={required()} />
              </ReferenceInput>
            )
          }
        </FormDataConsumer>
        <BooleanInput source="custom" />
        <Typography variant="h5">{translate('resources.software_releases.create.software_components')}</Typography>
        <SoftwareComponentInput />
      </SimpleForm>
    </Create>
  );
};
